import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Warning, ClockCountdown, DownloadSimple, CaretDown, CaretUp, Package, User, CalendarBlank,
  MagnifyingGlass, CaretLeft, CaretRight,
} from '@phosphor-icons/react';
import { useAuth } from '../App';
import { SkeletonBlock } from './module-ui';
import { apiPath } from '../config/apiBase';
import { notifySuccess, notifyError } from '../lib/notify';

const TICKETS_API = apiPath('/api/tickets');
const PAGE_SIZE = 10;

const STATE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'breached', label: 'Breached' },
  { id: 'at_risk', label: 'At risk' },
  { id: 'assignment_breached', label: 'Assignment breached' },
  { id: 'assignment_at_risk', label: 'Assignment at risk' },
];

const STATE_COLORS = {
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  assignment_breached: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  assignment_at_risk: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
};

const BULK_STATUSES = [
  { value: 'in_progress', label: 'In Progress', needsReason: false },
  { value: 'pending', label: 'Pending', needsReason: true },
  { value: 'completed', label: 'Completed', needsReason: true },
  { value: 'closed', label: 'Closed', needsReason: true },
  { value: 'cancelled', label: 'Cancelled', needsReason: true },
];

function authConfig() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {(state || '').replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="SLA pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Shared SLA breached / at-risk pipeline with API search + pagination.
 * Managers can multi-select tickets (across pages) and bulk-update status.
 */
export default function SlaPipelinePanel({
  compact = false,
  initialState = 'all',
  showExport = true,
  embedTitle = true,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canBulk = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [state, setState] = useState(initialState);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('in_progress');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkResolution, setBulkResolution] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const pageSize = compact ? 8 : PAGE_SIZE;
  const needsReason = useMemo(
    () => !!BULK_STATUSES.find((s) => s.value === bulkStatus)?.needsReason,
    [bulkStatus],
  );
  const needsRca = bulkStatus === 'completed';

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        state: state || 'all',
        page,
        page_size: pageSize,
      };
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/sla-pipeline`, {
        ...authConfig(),
        params,
      });
      setPayload(res.data);
    } catch (e) {
      console.error('SLA pipeline fetch failed', e);
      setError('Could not load SLA pipeline');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [state, compact, page, pageSize, q]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  useEffect(() => {
    setState(initialState || 'all');
    setPage(1);
  }, [initialState]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const items = payload?.items || [];
  const summary = payload?.summary || {};
  const pagination = payload?.pagination || {
    page: 1,
    page_size: pageSize,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false,
  };

  const pageTicketNos = useMemo(() => items.map((i) => i.ticket_no), [items]);
  const allPageSelected = pageTicketNos.length > 0
    && pageTicketNos.every((tn) => selected.has(tn));
  const somePageSelected = pageTicketNos.some((tn) => selected.has(tn));

  const toggleExpand = (ticketNo) => {
    setExpanded((prev) => ({ ...prev, [ticketNo]: !prev[ticketNo] }));
  };

  const changeState = (next) => {
    setState(next);
    setPage(1);
    setExpanded({});
  };

  const runSearch = (e) => {
    e?.preventDefault?.();
    const next = searchInput.trim();
    if (next === q) {
      fetchPipeline();
      return;
    }
    setPage(1);
    setQ(next);
  };

  const toggleSelect = (ticketNo) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ticketNo)) next.delete(ticketNo);
      else next.add(ticketNo);
      return next;
    });
  };

  const toggleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageTicketNos.forEach((tn) => next.delete(tn));
      } else {
        pageTicketNos.forEach((tn) => next.add(tn));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkStatus = async () => {
    const ticketNos = Array.from(selected);
    if (!ticketNos.length) {
      notifyError('Select at least one ticket');
      return;
    }
    if (needsReason && !bulkReason.trim()) {
      notifyError('Reason is required for this status');
      return;
    }
    if (needsRca && !bulkResolution.trim()) {
      notifyError('Reason for resolution is required when completing tickets');
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await axios.post(
        `${TICKETS_API}/bulk/status`,
        {
          ticket_nos: ticketNos,
          status: bulkStatus,
          update_reason: bulkReason.trim() || null,
          reason_for_resolution: bulkResolution.trim() || null,
        },
        authConfig(),
      );
      const ok = res.data?.success_count || 0;
      const failed = res.data?.failed || [];
      if (ok) {
        notifySuccess(`Updated ${ok} ticket(s) to ${bulkStatus.replace(/_/g, ' ')}`);
      }
      if (failed.length) {
        const sample = failed.slice(0, 3).map((f) => `${f.ticket_no}: ${f.error}`).join('; ');
        notifyError(
          `${failed.length} failed${sample ? ` — ${sample}` : ''}${failed.length > 3 ? '…' : ''}`,
        );
      }
      if (!ok && !failed.length) {
        notifyError('No tickets were updated');
      }
      clearSelection();
      setBulkReason('');
      setBulkResolution('');
      await fetchPipeline();
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Bulk status update failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = { state: state || 'all' };
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/sla-pipeline/export.csv`, {
        ...authConfig(),
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sla-pipeline-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('SLA export failed', e);
      notifyError('Failed to export SLA data');
    }
  };

  if (loading && !payload) {
    return <SkeletonBlock rows={compact ? 4 : 8} />;
  }

  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);
  const selectedCount = selected.size;

  return (
    <div className={`sla-pipeline-panel${compact ? ' is-compact' : ''}`} data-testid="sla-pipeline-panel">
      {embedTitle && (
        <div className="sla-pipeline-header">
          <div>
            <div className="sla-pipeline-title">
              <Warning size={18} weight="fill" color="#B91C1C" />
              SLA pipeline
            </div>
            <p className="sla-pipeline-subtitle">
              Track breached &amp; at-risk tickets pin-to-pin — what, who, when, which product.
              {payload?.generated_at_ist ? ` · Updated ${payload.generated_at_ist}` : ''}
            </p>
          </div>
          <div className="sla-pipeline-actions">
            {showExport && (
              <button type="button" className="tk-btn tk-btn-secondary" onClick={handleExport} data-testid="sla-export-button">
                <DownloadSimple size={16} />
                Export CSV
              </button>
            )}
            {compact && (
              <button
                type="button"
                className="tk-btn tk-btn-primary"
                onClick={() => navigate('/dashboard/sla?tab=pipeline')}
                data-testid="sla-view-all-button"
              >
                Open full pipeline
              </button>
            )}
          </div>
        </div>
      )}

      <div className="sla-pipeline-summary" role="group" aria-label="SLA summary">
        {[
          { id: 'breached', label: 'Breached', value: summary.breached || 0 },
          { id: 'at_risk', label: 'At risk', value: summary.at_risk || 0 },
          { id: 'assignment_breached', label: 'Assign breached', value: summary.assignment_breached || 0 },
          { id: 'assignment_at_risk', label: 'Assign at risk', value: summary.assignment_at_risk || 0 },
        ].map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`sla-summary-chip${state === chip.id ? ' is-active' : ''}`}
            onClick={() => changeState(state === chip.id ? 'all' : chip.id)}
            data-testid={`sla-chip-${chip.id}`}
          >
            <span className="sla-summary-chip-value">{chip.value}</span>
            <span className="sla-summary-chip-label">{chip.label}</span>
          </button>
        ))}
      </div>

      {!compact && (
        <div className="sla-pipeline-filters">
          <div className="sla-state-tabs" role="tablist">
            {STATE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={state === f.id}
                className={state === f.id ? 'is-active' : ''}
                onClick={() => changeState(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <form className="sla-api-search" onSubmit={runSearch} role="search">
            <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search ticket no, subject, person, product…"
              data-testid="sla-api-search-input"
              aria-label="Search SLA tickets"
            />
            <button type="submit" className="tk-btn tk-btn-secondary" data-testid="sla-api-search-button">
              Search
            </button>
          </form>
        </div>
      )}

      {canBulk && !compact && items.length > 0 && (
        <div className="sla-bulk-bar" data-testid="sla-bulk-bar">
          <label className="sla-bulk-select-all">
            <input
              type="checkbox"
              checked={allPageSelected}
              ref={(el) => {
                if (el) el.indeterminate = somePageSelected && !allPageSelected;
              }}
              onChange={toggleSelectPage}
              data-testid="sla-select-page"
            />
            <span>Select page</span>
          </label>
          <span className="sla-bulk-count" data-testid="sla-selected-count">
            {selectedCount} selected
            {selectedCount > pageTicketNos.length ? ' (across pages)' : ''}
          </span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="sla-bulk-status"
            disabled={!selectedCount || bulkSubmitting}
            data-testid="sla-bulk-status"
            aria-label="Bulk status"
          >
            {BULK_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {needsReason && (
            <input
              type="text"
              className="sla-bulk-reason"
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder={needsRca ? 'Reason for cause (required)' : 'Reason (required)'}
              disabled={!selectedCount || bulkSubmitting}
              data-testid="sla-bulk-reason"
            />
          )}
          {needsRca && (
            <input
              type="text"
              className="sla-bulk-reason"
              value={bulkResolution}
              onChange={(e) => setBulkResolution(e.target.value)}
              placeholder="Reason for resolution (required)"
              disabled={!selectedCount || bulkSubmitting}
              data-testid="sla-bulk-resolution"
            />
          )}
          <button
            type="button"
            className="tk-btn tk-btn-primary"
            disabled={
              !selectedCount
              || bulkSubmitting
              || (needsReason && !bulkReason.trim())
              || (needsRca && !bulkResolution.trim())
            }
            onClick={handleBulkStatus}
            data-testid="sla-bulk-apply"
          >
            {bulkSubmitting ? 'Updating…' : `Update ${selectedCount || ''} status`}
          </button>
          {selectedCount > 0 && (
            <button
              type="button"
              className="tk-btn tk-btn-secondary"
              onClick={clearSelection}
              disabled={bulkSubmitting}
              data-testid="sla-bulk-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {error && (
        <p style={{ color: '#B91C1C', fontSize: 13 }}>{error}</p>
      )}

      {loading && payload ? (
        <p style={{ fontSize: 12, color: '#6B7280', margin: '8px 0' }}>Loading…</p>
      ) : null}

      {items.length === 0 ? (
        <div className="sla-pipeline-empty">
          <ClockCountdown size={28} color="#9CA3AF" />
          <p>
            {q
              ? `No SLA tickets match “${q}”.`
              : 'No SLA issues in this view. All open tickets are on track.'}
          </p>
        </div>
      ) : (
        <>
          <div className={`sla-pipeline-grid${compact ? ' is-compact' : ''}`}>
            {items.map((item) => {
              const open = !!expanded[item.ticket_no];
              const isSelected = selected.has(item.ticket_no);
              const productName = item.product?.product_name || item.product?.product_key || '—';
              const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
              return (
                <div
                  key={item.ticket_no}
                  className={`sla-pipeline-card${open ? ' is-expanded' : ''}${isSelected ? ' is-selected' : ''}`}
                  data-testid="sla-pipeline-row"
                >
                  <div className="sla-pipeline-row-main">
                    <div className="sla-pipeline-row-top">
                      {canBulk && !compact && (
                        <label className="sla-card-check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(item.ticket_no)}
                            aria-label={`Select ${item.ticket_no}`}
                            data-testid="sla-row-select"
                          />
                        </label>
                      )}
                      <Link
                        to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                        className="sla-ticket-link"
                      >
                        {item.ticket_no}
                      </Link>
                      <StateBadge state={item.primary_state} />
                      {item.what?.priority && (
                        <span className={`badge badge-${item.what.priority}`}>{item.what.priority}</span>
                      )}
                    </div>
                    <div className="sla-pipeline-what" title={item.what?.subject}>
                      <strong>What:</strong> {item.what?.subject || '—'}
                    </div>
                    <div className="sla-pipeline-meta">
                      <span><User size={14} /> <strong>Who:</strong> {assignees}</span>
                      <span><CalendarBlank size={14} /> <strong>When:</strong> {item.when?.due_date_ist || '—'}</span>
                      <span><Package size={14} /> <strong>Product:</strong> {productName}</span>
                      {item.when?.hours_overdue > 0 && (
                        <span style={{ color: '#B91C1C', fontWeight: 600 }}>
                          {item.when.hours_overdue}h overdue
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="sla-pipeline-row-actions">
                    <button
                      type="button"
                      className="tk-btn tk-btn-secondary"
                      onClick={() => toggleExpand(item.ticket_no)}
                      aria-expanded={open}
                      data-testid="sla-expand-pipeline"
                    >
                      {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                      Pipeline
                    </button>
                  </div>
                  {open && (
                    <div className="sla-pipeline-detail">
                      <div className="sla-pipeline-detail-grid">
                        <div>
                          <h4>Reporter</h4>
                          <p>{item.who?.reporter_name || '—'}{item.who?.reporter_email ? ` · ${item.who.reporter_email}` : ''}</p>
                        </div>
                        <div>
                          <h4>Assignees</h4>
                          <p>
                            {(item.who?.assignees || []).length
                              ? item.who.assignees.map((a) => `${a.name}${a.email ? ` <${a.email}>` : ''}`).join('; ')
                              : '—'}
                          </p>
                        </div>
                        <div>
                          <h4>Created</h4>
                          <p>{item.when?.created_at_ist || '—'} · {item.who?.created_by || '—'}</p>
                        </div>
                        <div>
                          <h4>SLA policy</h4>
                          <p>
                            {item.sla_policy?.name || item.sla_policy?.sla_policy_id || '—'}
                            {item.sla_policy?.resolution_time_hours != null
                              ? ` · resolve ${item.sla_policy.resolution_time_hours}h`
                              : ''}
                          </p>
                        </div>
                      </div>
                      <PipelineTrack pins={item.pipeline || []} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!compact && (
            <div className="sla-pagination" data-testid="sla-pagination">
              <span className="sla-pagination-meta">
                Showing {from}–{to} of {pagination.total}
                {q ? ` for “${q}”` : ''}
              </span>
              <div className="sla-pagination-buttons">
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  disabled={!pagination.has_prev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="sla-prev-page"
                >
                  <CaretLeft size={16} />
                  Previous
                </button>
                <span className="sla-pagination-page">
                  Page {pagination.page} / {pagination.total_pages}
                </span>
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  disabled={!pagination.has_next || loading}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="sla-next-page"
                >
                  Next
                  <CaretRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
